import { useCallback, useEffect, useRef, useState } from "react";
import {
    FalkonDocUploadDateIcon,
    FalkonDocUploadSignIcon,
    FalkonDocUploadTextIcon,
    FalkonDocUploadCheckboxIcon,
    FalkonDocInitialIcon,
} from "../../icon";
import DraggableField from "./draggable-field.component";
import ButtonComponent from "../shared/button/button.component";
import { buttonLabels, ToasterTexts } from "../shared/constants";
import { mapToSecureDocUpdateInfo, SecureDocUpdateInfoModel } from "../../models/secure-docs.model";
import { SecureDocsService } from "../../services/SecureDocsService";
import setToast from "../../helpers/toaster.helper";
import { ToasterType } from "../shared/toaster/toaster.model";
import { Autocomplete, TextField } from "@mui/material";
import { AppRoutes } from "../routes/route.path";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import * as selector from '../../selectors/selectors';

export interface FieldType {
    id: string;
    type: string;
    label: string;
    icon: JSX.Element;
}

export const fieldTypes: FieldType[] = [
    {
        id: "signature",
        type: "signature",
        label: "Signature",
        icon: <FalkonDocUploadSignIcon />,
    },
    {
        id: "initials",
        type: "initials",
        label: "Initials",
        icon: <FalkonDocInitialIcon />,
    },
    {
        id: "text",
        type: "text",
        label: "Text",
        icon: <FalkonDocUploadTextIcon />,
    },
    {
        id: "date",
        type: "date",
        label: "Date",
        icon: <FalkonDocUploadDateIcon />,
    },
    {
        id: "checkbox",
        type: "checkbox",
        label: "Checkbox",
        icon: <FalkonDocUploadCheckboxIcon />,
    },
];

export interface DroppedField {
    id: string;
    type: string;
    label: string;
    x: number;
    y: number;
    width: number;
    height: number;
    page?: number;
    assignedRecipient?: string;
}

interface SidebarProps {
    onFieldDragStart: (
        field: FieldType,
        startPos: { x: number; y: number },
        offset: { x: number; y: number }
    ) => void;
    isUpdateMode?: boolean;
    fileID?: string;
    droppedFields: DroppedField[];
    selectedRecipient: { recipient_id: string, email: string; order_number: number } | null;
    onRecipientChange: (recipient: { recipient_id: string, email: string; order_number: number } | null) => void;
    recipientEmails?: { recipient_id: string, email: string; order_number: number }[];
    onSaveDocument?: () => Promise<void>;
    recipientRestrictions?: Map<string, { canEdit: boolean, canAdd: boolean }>;
}

const SidebarComponent: React.FC<SidebarProps> = (props) => {
    const borderColors = ["#156fb3", "#7A14FF", "#00C48C", "#0091FF", "#FF647C"];
    const IsMobileDevice: boolean = useSelector(selector.isMobileDevice);
    const IsDarkMode: boolean = useSelector(selector.isDarkMode);
    const [updateData, setUpdateData] = useState<SecureDocUpdateInfoModel | null>(null);
    const [isLoadingUpdateData, setIsLoadingUpdateData] = useState<boolean>(false);
    const [emails, setEmails] = useState<{ recipient_id: string, email: string; order_number: number }[]>([]);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [selectedRecipient, setSelectedRecipient] = useState<{ recipient_id: string, email: string; order_number: number } | null>(
        props.selectedRecipient
    );
    const navigate = useNavigate();
    const [fileID, setFileID] = useState<string>(props.fileID);
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const [allRecipientsConfigured, setAllRecipientsConfigured] = useState<boolean>(false);
    const [activeFieldId, setActiveFieldId] = useState<string | null>(null);

    const getRecipientColor = (orderNumber: number): string => {
        return borderColors[(orderNumber - 1) % borderColors.length];
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (fileID) {
            setIsLoadingUpdateData(true);
            SecureDocsService.getSignedSecureDocUpdateInfo(fileID)
                .then((res) => {
                    const mappedData = mapToSecureDocUpdateInfo(res.data);
                    setUpdateData(mappedData);
                })
                .catch((error) => {
                    setToast("Failed to load document data", ToasterType.DANGER);
                })
                .finally(() => {
                    setIsLoadingUpdateData(false);
                });
        }
    }, [fileID]);

    useEffect(() => {
        if (updateData?.Recipients?.length) {
            const formattedRecipients = updateData.Recipients.map((r: any) => ({
                recipient_id: r.RecipientID,
                email: r.Email,
                order_number: r.OrderNumber,
            }));
            setEmails(formattedRecipients);
        }
    }, [updateData]);

    useEffect(() => {
        setSelectedRecipient(props.selectedRecipient);
    }, [props.selectedRecipient]);

    const handleSubmit = async () => {
        try {
            setIsSubmitting(true);

            if (props.onSaveDocument) {
                await props.onSaveDocument();
            } else {
            }
        } catch (error) {
            setToast(ToasterTexts.SECURE_DOC_SAVE_DOC_FAILED, ToasterType.DANGER);
        } finally {
            setIsSubmitting(false);
        }
    };

    const checkAllRecipientsConfigured = useCallback(() => {
        const availableRecipients = (emails && emails.length > 0) ? emails : (props.recipientEmails || []);

        if (availableRecipients.length === 0) {
            return false;
        }

        const recipientIds = availableRecipients.map(r => r.recipient_id);
        const assignedRecipientIds = new Set(
            props.droppedFields
                .filter(field => field.assignedRecipient)
                .map(field => field.assignedRecipient)
        );

        return recipientIds.every(id => assignedRecipientIds.has(id));
    }, [emails, props.recipientEmails, props.droppedFields]);

    useEffect(() => {
        setAllRecipientsConfigured(checkAllRecipientsConfigured());
    }, [checkAllRecipientsConfigured]);

    const handleRecipientChange = (recipient: { recipient_id: string, email: string; order_number: number } | null) => {
        setSelectedRecipient(recipient);
        props.onRecipientChange(recipient);
    };

    const canAddFieldsForRecipient = (recipientId: string): boolean => {
        if (!props.recipientRestrictions) return true;
        const restrictions = props.recipientRestrictions.get(recipientId);
        return restrictions?.canAdd !== false;
    };

    const getRecipientStatus = (recipientId: string): string => {
        if (!props.recipientRestrictions) return "";
        const restrictions = props.recipientRestrictions.get(recipientId);
        if (restrictions?.canAdd === false) return " (Invited - Cannot modify)";
        return "";
    };

    return (
        <div className={`${IsDarkMode ? "bg-color-sidebar-bg" : "bg-color-secondary"} d-flex flex-direction align-center  sidebar  o-x-hidden ${IsMobileDevice ? 'px-3 pt-3 w-p-100' : "p-3"}`}>
            <div className="recipient-section">
                <label className="color-primary-accent h4-semibold pb-2 t-align-left">
                    Select Recipient
                    <div className="pt-1">
                        <Autocomplete
                            size="small"
                            className={""}
                            classes={{
                                input: "h4-regular color-text-field-font",
                                paper: "bg-color-menu-bg box-shadow-none dialogue-shadow",
                            }}
                            options={(emails && emails.length > 0) ? emails : (props.recipientEmails || [])}
                            value={selectedRecipient || null}
                            onChange={(e, value) => {
                                handleRecipientChange(value);
                            }}
                            getOptionLabel={(option) => `${option.email}${getRecipientStatus(option.recipient_id)}`}
                            renderOption={(props, option) => (
                                <li {...props}>
                                    <div className="recipient-section">
                                        <div className="recipient-box d-flex align-center py-1 w-p-100">
                                            <div
                                                className="recipient-avatar d-flex align-center justify-center h5-bold mr-1"
                                                style={{ backgroundColor: getRecipientColor(option.order_number) }}
                                            >
                                                {option.order_number}
                                            </div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <span
                                                    className="recipient-email color-primary-accent h4-medium c-pointer"
                                                    title={option.email}
                                                >
                                                    {option.email}
                                                </span>
                                                {getRecipientStatus(option.recipient_id) && (
                                                    <div className="recipient-status h7-regular color-primary-accent-alt">
                                                        {getRecipientStatus(option.recipient_id)}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    size="small"
                                    variant="outlined"
                                    placeholder="Select Recipient"
                                    name="otp"
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: selectedRecipient && (
                                            <div
                                                className="order-number-circle d-flex align-center justify-center h6-bold"
                                                style={{ backgroundColor: getRecipientColor(selectedRecipient.order_number) }}
                                            >
                                                {selectedRecipient.order_number}
                                            </div>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </div>
                </label>

                {selectedRecipient && !canAddFieldsForRecipient(selectedRecipient.recipient_id) && (
                    <div className="recipient-warning mt-1 p-1 h5-regular">
                        This recipient has been invited. You cannot add new fields or modify existing ones.
                    </div>
                )}
            </div>

            <div className="">
                <hr className="horizontal-line"></hr>
            </div>

            <div className="field-list d-flex flex-direction mt-1">
                <div className="color-primary-accent h4-semibold t-align-left pb-0">
                    Field Types
                </div>
                {fieldTypes.map((field) => (
                    <div
                    key={field.id}
                    className="relative"
                    onMouseDown={() => setActiveFieldId(field.id)}
                    onMouseUp={() => setActiveFieldId(null)}
                    onMouseLeave={() => setActiveFieldId(null)}
                    >
                    <DraggableField
                        field={field}
                        onDragStart={props.onFieldDragStart}
                        disabled={selectedRecipient ? !canAddFieldsForRecipient(selectedRecipient.recipient_id) : false}
                        isActive={activeFieldId === field.id}
                    />
                    {selectedRecipient && !canAddFieldsForRecipient(selectedRecipient.recipient_id) && (
                        <div className="absolute top-2 right-2 text-lg">🔒</div>
                    )}
                    </div>
                ))}
            </div>

            <div className={`d-flex  align-center justify-center ${IsMobileDevice ? 'w-p-100 mt-1' : ""}`}>
                <ButtonComponent
                    OnClick={handleSubmit}
                    ButtonLabel={buttonLabels.SECURE_DOC_SIGN}
                    ClassName={"send-sidebar-button py-1"}
                    Variant={isSubmitting || !allRecipientsConfigured ? "disabled-btn-small" : "primary-btn-small"}
                />
            </div>

        </div>
    );
};

export default SidebarComponent;